import React, { useState, useCallback, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../contexts/ThemeContext';

interface CustomTooltipProps {
    children: React.ReactNode;
    text?: string;
    content?: React.ReactNode;
    position?: 'top' | 'bottom' | 'left' | 'right';
    /**
     * When true, tooltip is rendered in document.body with position:fixed (escapes overflow:hidden ancestors).
     * Position is derived from the trigger element’s bounding rect.
     */
    portal?: boolean;
    /** When true, tooltip is fixed to the right of the pointer (uses document portal; ignores position). */
    anchorToCursor?: boolean;
    /** Pixels to the right of the cursor when anchorToCursor is true. */
    cursorOffsetX?: number;
    /**
     * Controlled visibility. When omitted the tooltip is *uncontrolled* and manages its own
     * hover/focus visibility internally — the drop-in replacement for a native `title` attribute
     * (`<CustomTooltip text="…"><button …/></CustomTooltip>`). Pass `show` only when a parent needs
     * to drive visibility (e.g. show a validation-error tooltip regardless of hover).
     */
    show?: boolean;
    minWidth?: string | number;
    maxWidth?: string | number;
    backgroundColor?: string;
    horizontalOffset?: number; // Additional horizontal offset in pixels (positive = move right)
    verticalOffset?: number; // Additional vertical offset in pixels (positive = move down)
}

/**
 * Custom tooltip component with a triangle arrow at the bottom (pointing down)
 * Based on the notes tooltip design
 */
const CustomTooltipComponent: React.FC<CustomTooltipProps> = ({
    children,
    text,
    content,
    position = 'top',
    portal = false,
    anchorToCursor = false,
    cursorOffsetX = 12,
    show: showProp,
    minWidth,
    maxWidth,
    backgroundColor,
    horizontalOffset = 0,
    verticalOffset = 0,
}) => {
    const theme = useTheme();
    const anchorRef = useRef<HTMLDivElement>(null);
    const [cursor, setCursor] = useState({ x: 0, y: 0 });

    // Uncontrolled mode: when the caller omits `show`, manage hover/focus visibility internally so
    // the component is a drop-in replacement for a native `title` attribute.
    const isControlled = showProp !== undefined;
    const [hovered, setHovered] = useState(false);
    const show = isControlled ? showProp : hovered;

    const handlePointerMove = useCallback(
        (e: React.MouseEvent) => {
            if (anchorToCursor && show) {
                setCursor({ x: e.clientX, y: e.clientY });
            }
        },
        [anchorToCursor, show]
    );

    const handlePointerEnter = useCallback(
        (e: React.MouseEvent) => {
            if (anchorToCursor) {
                setCursor({ x: e.clientX, y: e.clientY });
            }
        },
        [anchorToCursor]
    );

    const [anchorPortalBox, setAnchorPortalBox] = useState<React.CSSProperties | null>(null);

    const positionAnchorPortal = useCallback(() => {
        if (!show || !portal || anchorToCursor) {
            setAnchorPortalBox(null);
            return;
        }
        const el = anchorRef.current;
        if (!el) {
            setAnchorPortalBox(null);
            return;
        }
        const rect = el.getBoundingClientRect();
        const spacing = parseInt(theme.spacing.xs.replace('px', '')) || 8;
        const h = horizontalOffset;
        const v = verticalOffset;
        const base: React.CSSProperties = {
            position: 'fixed',
            zIndex: 10002,
            minWidth: minWidth || (content ? '150px' : 'auto'),
            maxWidth: maxWidth || (content ? '300px' : 'none'),
            width: 'max-content',
            pointerEvents: 'none',
        };
        switch (position) {
            case 'right':
                setAnchorPortalBox({
                    ...base,
                    left: rect.right + spacing + h,
                    top: rect.top + rect.height / 2 + v,
                    transform: 'translateY(-50%)',
                });
                break;
            case 'left':
                setAnchorPortalBox({
                    ...base,
                    left: rect.left - spacing + h,
                    top: rect.top + rect.height / 2 + v,
                    transform: 'translate(-100%, -50%)',
                });
                break;
            case 'top':
                setAnchorPortalBox({
                    ...base,
                    left: rect.left + rect.width / 2 + h,
                    top: rect.top - spacing + v,
                    transform: 'translate(-50%, -100%)',
                });
                break;
            case 'bottom':
                setAnchorPortalBox({
                    ...base,
                    left: rect.left + rect.width / 2 + h,
                    top: rect.bottom + spacing + v,
                    transform: 'translate(-50%, 0)',
                });
                break;
            default:
                setAnchorPortalBox(null);
        }
    }, [
        show,
        portal,
        anchorToCursor,
        position,
        horizontalOffset,
        verticalOffset,
        minWidth,
        maxWidth,
        content,
        theme.spacing.xs,
        text,
    ]);

    useLayoutEffect(() => {
        positionAnchorPortal();
    }, [positionAnchorPortal]);

    useEffect(() => {
        if (!show || !portal || anchorToCursor) return;
        positionAnchorPortal();
        window.addEventListener('scroll', positionAnchorPortal, true);
        window.addEventListener('resize', positionAnchorPortal);
        return () => {
            window.removeEventListener('scroll', positionAnchorPortal, true);
            window.removeEventListener('resize', positionAnchorPortal);
        };
    }, [show, portal, anchorToCursor, positionAnchorPortal]);

    // Arrow points toward the element (up when tooltip is below, down when tooltip is above, left when tooltip is right, right when tooltip is left)
    const getArrowStyle = (): React.CSSProperties => {
        const bgColor = backgroundColor || theme.colors.surface;
        
        if (position === 'bottom') {
            // Arrow at top of tooltip, pointing up
            return {
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderBottom: `6px solid ${bgColor}`,
            };
        } else if (position === 'top') {
            // Arrow at bottom of tooltip, pointing down
            return {
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: `6px solid ${bgColor}`,
            };
        } else if (position === 'right') {
            // Arrow at left of tooltip, pointing left
            return {
                position: 'absolute',
                right: '100%',
                top: '50%',
                transform: 'translateY(-50%)',
                width: 0,
                height: 0,
                borderTop: '6px solid transparent',
                borderBottom: '6px solid transparent',
                borderRight: `6px solid ${bgColor}`,
            };
        } else if (position === 'left') {
            // Arrow at right of tooltip, pointing right
            return {
                position: 'absolute',
                left: '100%',
                top: '50%',
                transform: 'translateY(-50%)',
                width: 0,
                height: 0,
                borderTop: '6px solid transparent',
                borderBottom: '6px solid transparent',
                borderLeft: `6px solid ${bgColor}`,
            };
        } else {
            // Default to pointing down
            return {
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: `6px solid ${bgColor}`,
            };
        }
    };

    const getTooltipPosition = (): React.CSSProperties => {
        // Parse spacing value (could be "8px" or "8")
        const baseSpacing = parseInt(theme.spacing.xs.replace('px', '')) || 8;
        const hOffset = horizontalOffset;
        const vOffset = verticalOffset;
        
        switch (position) {
            case 'top':
                return {
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: `translate(calc(-50% + ${hOffset}px), ${-vOffset}px)`,
                    marginBottom: `${baseSpacing}px`,
                };
            case 'bottom':
                return {
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: `translate(calc(-50% + ${hOffset}px), ${vOffset}px)`,
                    marginTop: `${baseSpacing}px`,
                };
            case 'left':
                return {
                    position: 'absolute',
                    right: '100%',
                    top: '50%',
                    transform: `translate(${-hOffset}px, calc(-50% + ${vOffset}px))`,
                    marginRight: `${baseSpacing}px`,
                };
            case 'right':
                return {
                    position: 'absolute',
                    left: '100%',
                    top: '50%',
                    transform: `translate(${hOffset}px, calc(-50% + ${vOffset}px))`,
                    marginLeft: `${baseSpacing}px`,
                };
        }
    };

    // For tooltips to position correctly, we need a positioned parent.
    // Use 'inline-block' for most cases, but wrap full-width elements properly
    // The tooltip width is independent of the trigger element size
    // Check if we need to use 'block' display for full-width containers
    const wrapperStyle: React.CSSProperties = { position: 'relative' };
    let needsBlockDisplay = false;

    // Try to detect if child needs block display
    try {
        const childElement = React.Children.only(children) as React.ReactElement<any>;
        const childStyle = (childElement?.props?.style as React.CSSProperties) || {};
        needsBlockDisplay = childStyle.width === '100%' || childStyle.display === 'block';
    } catch {
        // If we can't detect, default to inline-block
        needsBlockDisplay = false;
    }

    if (needsBlockDisplay) {
        wrapperStyle.display = 'block';
        wrapperStyle.width = '100%';
        wrapperStyle.height = '100%';
    } else {
        wrapperStyle.display = 'inline-block';
    }

    // Render text with newlines properly handled
    const renderText = () => {
        if (content) {
            return content;
        }
        if (!text) {
            return null;
        }
        // Check for newline character (both literal \n and actual newline)
        const hasNewline = text.includes('\n') || text.includes('\\n');
        if (hasNewline) {
            // Replace escaped \n with actual newline, then split
            const normalizedText = text.replace(/\\n/g, '\n');
            // Split by newlines and render each line separately
            return normalizedText.split('\n').map((line, index, array) => (
                <React.Fragment key={index}>
                    {line}
                    {index < array.length - 1 && <br />}
                </React.Fragment>
            ));
        }
        return text;
    };

    const bgColor = backgroundColor || theme.colors.surface;

    const innerBoxStyle: React.CSSProperties = {
        position: 'relative',
        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
        backgroundColor: bgColor,
        border: `1px solid ${theme.colors.border.default}`,
        borderRadius: theme.borderRadius.sm,
        boxShadow: theme.shadows.lg,
        fontSize: theme.typography.fontSize.sm,
        color: theme.colors.text.primary,
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        pointerEvents: 'none',
        width: '100%',
        boxSizing: 'border-box',
    };

    /** Arrow on the left edge of the tooltip, pointing toward the cursor. */
    const cursorArrowStyle: React.CSSProperties = {
        position: 'absolute',
        left: -6,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 0,
        height: 0,
        borderTop: '6px solid transparent',
        borderBottom: '6px solid transparent',
        borderRight: `6px solid ${bgColor}`,
    };

    const cursorPortal =
        anchorToCursor && show && typeof document !== 'undefined'
            ? createPortal(
                  <div
                      style={{
                          position: 'fixed',
                          left: cursor.x + cursorOffsetX + horizontalOffset,
                          top: cursor.y + verticalOffset,
                          transform: 'translateY(-50%)',
                          zIndex: 10002,
                          minWidth: minWidth || (content ? '150px' : 'auto'),
                          maxWidth: maxWidth || (content ? '300px' : 'none'),
                          width: 'max-content',
                          pointerEvents: 'none',
                      }}
                  >
                      <div style={innerBoxStyle}>
                          {renderText()}
                          <div style={cursorArrowStyle} />
                      </div>
                  </div>,
                  document.body
              )
            : null;

    const anchorTargetPortal =
        portal && show && !anchorToCursor && anchorPortalBox && typeof document !== 'undefined'
            ? createPortal(
                  <div style={anchorPortalBox}>
                      <div style={innerBoxStyle}>
                          {renderText()}
                          <div style={getArrowStyle()} />
                      </div>
                  </div>,
                  document.body
              )
            : null;

    return (
        <div
            ref={anchorRef}
            style={wrapperStyle}
            onMouseMove={anchorToCursor ? handlePointerMove : undefined}
            onMouseEnter={(e) => {
                if (anchorToCursor) handlePointerEnter(e);
                else if (portal) positionAnchorPortal();
                if (!isControlled) setHovered(true);
            }}
            onMouseLeave={!isControlled ? () => setHovered(false) : undefined}
            onFocus={!isControlled ? () => setHovered(true) : undefined}
            onBlur={!isControlled ? () => setHovered(false) : undefined}
        >
            {children}
            {show && !anchorToCursor && !portal && (
                <div
                    style={{
                        position: 'absolute',
                        ...getTooltipPosition(),
                        zIndex: 10001,
                        minWidth: minWidth || (content ? '150px' : 'auto'),
                        maxWidth: maxWidth || (content ? '300px' : 'none'),
                        width: 'max-content',
                    }}
                >
                    <div style={innerBoxStyle}>
                        {renderText()}
                        <div style={getArrowStyle()} />
                    </div>
                </div>
            )}
            {anchorTargetPortal}
            {cursorPortal}
        </div>
    );
};

// Memoize to prevent unnecessary re-renders when props haven't changed
export const CustomTooltip = React.memo(CustomTooltipComponent);
